import { Injectable } from '@angular/core';
import { dedupeEntries, discoverCustomKeys, isEmptyValue, isObjectIdLike, toLeadRef } from '../helpers/merge.helpers';
import { CONTACT_FIELD_KEYS, customScalarField, DUPLICATE_MERGE_FIELDS, KNOWN_FIELD_KEYS } from '../models/lead-field-registry';
import {
  ComparisonCell,
  ComparisonRow,
  DuplicateLead,
  FieldDefinition,
  FieldKind,
  LeadComparisonResult,
  LeadId,
  MergeableFieldComparison,
  MergeEntry,
  ScalarAlternative,
  ScalarFieldComparison,
  ScalarStatus,
} from '../models/duplicate-lead-merge.types';

/**
 * Builds the read-only comparison model used by Step 2. Pure and stateless:
 * given the kept lead and the duplicate set it returns what differs, what is
 * missing, and what can be merged. It never mutates a lead and never decides to
 * copy anything — selection is the user's job.
 */
@Injectable()
export class LeadComparisonService {
  /**
   * @param keepLead   the lead the user chose to retain
   * @param duplicates every other detected duplicate (kept lead excluded)
   */
  compare(keepLead: DuplicateLead, duplicates: DuplicateLead[]): LeadComparisonResult {
    const fields = this.resolveFields([keepLead, ...duplicates]);

    const scalars: ScalarFieldComparison[] = [];
    const mergeables: MergeableFieldComparison[] = [];

    for (const field of fields) {
      if (field.kind === FieldKind.Scalar) {
        const cmp = this.compareScalar(field, keepLead, duplicates);
        // Only surface fields that actually offer something to copy.
        if (cmp.status !== 'same' && cmp.alternatives.length > 0) scalars.push(cmp);
      } else {
        const cmp = this.compareMergeable(field, keepLead, duplicates);
        if (cmp.candidates.length > 0) mergeables.push(cmp);
      }
    }

    const totalItems = scalars.length + mergeables.length;
    const rows = this.buildRows(fields, [keepLead, ...duplicates]);
    return { keepLeadId: keepLead._id, rows, scalars, mergeables, totalItems };
  }

  /**
   * The compare grid: one row per field where the leads DISAGREE. Fields every
   * lead holds the same value for are dropped — the grid only surfaces what is
   * different (or missing on some leads), so there is nothing to scan past.
   * `leads[0]` is the kept/recommended lead; each cell is flagged `different`
   * when it doesn't match it (used to highlight the diverging values).
   */
  private buildRows(fields: FieldDefinition[], leads: DuplicateLead[]): ComparisonRow[] {
    const rows: ComparisonRow[] = [];
    const keepId = leads[0]?._id;

    for (const field of fields) {
      const cells: Record<LeadId, ComparisonCell> = {};
      let anyValue = false;

      for (const lead of leads) {
        const display = this.cellDisplay(field, lead);
        if (display) anyValue = true;
        cells[lead._id] = { display, blank: !display, different: false };
      }
      if (!anyValue) continue;

      // Row is "different" when the leads hold more than one distinct value
      // (a blank counts as its own value — a missing field is a difference too).
      const keepDisplay = (cells[keepId]?.display ?? '').trim().toLowerCase();
      const distinct = new Set(leads.map(l => (cells[l._id]?.display ?? '').trim().toLowerCase()));
      const different = distinct.size > 1;
      // Every lead agrees — nothing to compare, hide it. Name/email/mobile stay so the agent can
      // always see who each lead is and open "Add As Alternative Details" from them.
      if (!different && !CONTACT_FIELD_KEYS.has(field.key)) continue;

      for (const lead of leads) {
        const cell = cells[lead._id];
        cell.different = (cell.display ?? '').trim().toLowerCase() !== keepDisplay;
      }

      rows.push({ key: field.key, label: field.label, kind: field.kind, different, cells });
    }

    return rows;
  }

  /** One cell's text: scalars render their value, list fields their first entry + overflow. */
  private cellDisplay(field: FieldDefinition, lead: DuplicateLead): string {
    const raw = field.read(lead);
    if (isEmptyValue(raw)) return '';

    if (field.kind === FieldKind.Mergeable) {
      const entries = field.toEntries ? field.toEntries(raw, toLeadRef(lead)) : [];
      if (!entries.length) return '';
      return entries.length > 1 ? `${entries[0].text} +${entries.length - 1} more` : entries[0].text;
    }

    const display = (field.display ?? (v => String(v ?? '')))(raw);
    // An unresolved ObjectId is noise, not information.
    return !display || isObjectIdLike(display) ? '' : display;
  }

  /** Registry fields plus any custom scalar fields discovered on the candidates. */
  private resolveFields(allLeads: DuplicateLead[]): FieldDefinition[] {
    const customKeys = discoverCustomKeys(allLeads, KNOWN_FIELD_KEYS);
    const customFields = customKeys.map(customScalarField);
    return [...DUPLICATE_MERGE_FIELDS, ...customFields];
  }

  private compareScalar(field: FieldDefinition, keepLead: DuplicateLead, duplicates: DuplicateLead[]): ScalarFieldComparison {
    const keepValue = field.read(keepLead);
    const keepBlank = isEmptyValue(keepValue);
    const equals = field.equals ?? ((a, b) => a === b);
    const display = field.display ?? (v => String(v ?? ''));

    const alternatives: ScalarAlternative[] = [];
    const seen = new Set<string>();

    for (const dup of duplicates) {
      const value = field.read(dup);
      if (isEmptyValue(value)) continue;
      if (!keepBlank && equals(keepValue, value)) continue; // same as kept — nothing to offer
      const disp = display(value);
      if (!disp || isObjectIdLike(disp)) continue; // never offer an unresolved raw id
      const dedupeKey = disp.toLowerCase();
      if (seen.has(dedupeKey)) continue; // collapse identical alternatives across duplicates
      seen.add(dedupeKey);
      alternatives.push({
        value: this.payloadValueFor(field, value, dup),
        display: disp,
        source: toLeadRef(dup),
        createdAt: dup.created_at,
        updatedAt: dup.updated_at,
      });
    }

    const status: ScalarStatus = keepBlank ? (alternatives.length ? 'empty' : 'same') : alternatives.length ? 'different' : 'same';

    return {
      kind: FieldKind.Scalar,
      key: field.key,
      label: field.label,
      group: field.group,
      custom: !!field.custom || field.group === 'Custom Fields',
      status,
      keepValue,
      keepDisplay: display(keepValue),
      keepUpdatedAt: keepLead.updated_at,
      alternatives,
    };
  }

  /**
   * The `name` field copies name parts as a unit, so its alternative value must
   * carry those parts rather than the display string.
   */
  private payloadValueFor(field: FieldDefinition, value: any, dup: DuplicateLead): any {
    switch (field.key) {
      case 'name':
        return { first_name: dup.first_name, last_name: dup.last_name, full_name: dup.full_name };
      case 'project':
        // Copy the id + human-readable siblings so the kept lead resolves the name too.
        return this.pick(dup, ['project', 'project_address', 'Project_Name', 'listing_project']);
      case 'agent_responsible':
        return this.pick(dup, ['agent_responsible', 'agent_responsible_name', 'agent_responsible_formatted', 'isAgentResponsibleTeam']);
      case 'owner':
        return this.pick(dup, ['Owner', 'owner', 'Owner_formatted', 'isOwnerTeam']);
      default:
        return value;
    }
  }

  /** Copy only the defined keys from a lead into a fresh payload fragment. */
  private pick(lead: DuplicateLead, keys: string[]): Record<string, any> {
    const out: Record<string, any> = {};
    for (const key of keys) {
      if (lead[key] !== undefined && lead[key] !== null) out[key] = lead[key];
    }
    return out;
  }

  private compareMergeable(field: FieldDefinition, keepLead: DuplicateLead, duplicates: DuplicateLead[]): MergeableFieldComparison {
    const toEntries = field.toEntries!;
    const keyOf = field.entryKey ?? ((e: MergeEntry) => e.id);

    const existing = toEntries(field.read(keepLead), toLeadRef(keepLead));
    const seen = new Set<string>(existing.map(keyOf));

    const incoming: MergeEntry[] = [];
    for (const dup of duplicates) {
      incoming.push(...toEntries(field.read(dup), toLeadRef(dup)));
    }
    const candidates = dedupeEntries(incoming, keyOf, seen);

    return {
      kind: FieldKind.Mergeable,
      key: field.key,
      label: field.label,
      group: field.group,
      custom: !!field.custom,
      existing,
      candidates,
    };
  }
}
